import { type FormEvent, useState } from 'react';
import { Navigate, useLocation } from 'react-router';
import { errorMessage } from '../../api/errors';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Card, Field, Icon, Input } from '../../design';
import s from './auth.module.css';

export function LoginPage() {
  const { state, login, verifyTotp } = useAuth();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (state.kind === 'authenticated') {
    const from = (location.state as { from?: string } | null)?.from ?? '/';
    return <Navigate to={from} replace />;
  }

  const submitPassword = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const c = await login(email.trim().toLowerCase(), password);
      setChallengeId(c.challengeId);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const submitCode = async (e: FormEvent) => {
    e.preventDefault();
    if (!challengeId) return;
    setBusy(true);
    setError(null);
    try {
      await verifyTotp(challengeId, code);
    } catch (err) {
      setError(errorMessage(err));
      setCode('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={s.page}>
      <Card className={s.card}>
        <div className={s.brand}>
          <span className={s.mark}>
            <Icon name="locker" size={22} />
          </span>
          <div>
            <strong>SYNAPTIC</strong>
            <span className="muted small">Smart Locker · Consolă de administrare</span>
          </div>
        </div>
        {challengeId === null ? (
          <form onSubmit={submitPassword} className="stack" aria-label="Pasul 1: parola">
            <h1>Autentificare</h1>
            <Field label="E-mail" htmlFor="login-email">
              <Input
                id="login-email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoFocus
              />
            </Field>
            <Field label="Parolă" htmlFor="login-password">
              <Input
                id="login-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={12}
              />
            </Field>
            {error && (
              <p className={s.error} role="alert">
                {error}
              </p>
            )}
            <Button variant="primary" type="submit" busy={busy}>
              Continuă
            </Button>
          </form>
        ) : (
          <form onSubmit={submitCode} className="stack" aria-label="Pasul 2: codul TOTP">
            <h1>Cod de verificare</h1>
            <p className="muted">Introdu codul din aplicația de autentificare pentru {email}.</p>
            <Field label="Cod TOTP" htmlFor="login-code">
              <Input
                id="login-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus
              />
            </Field>
            {error && (
              <p className={s.error} role="alert">
                {error}
              </p>
            )}
            <div className="flex">
              <Button
                onClick={() => {
                  setChallengeId(null);
                  setCode('');
                  setError(null);
                }}
              >
                Înapoi
              </Button>
              <Button variant="primary" type="submit" busy={busy} disabled={code.length !== 6}>
                Autentifică-mă
              </Button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
}
